/**
 * @file ConversationInfoPanel.tsx
 * @description Сведения о диалоге (chat-window-design.md §1в): клиент,
 * заказ со стадией, суммой и тем, кто ведёт заказ, диалог («Ведёт» и
 * [Взять себе] [Передать…]), «История диалога» — только служебные записи,
 * нажатие прокручивает ленту к записи. Место под время ответа (R4.14).
 * Третья зона развёрнутого окна от 1280, шторка «Сведения» в двух зонах.
 */

import { ChevronRight } from "lucide-react";
import { useState, type ReactNode } from "react";

import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import type { ChatFeedItem, ConversationDetail, ConversationEventDto } from "../../lib/api-chat";
import { EVENT_TONE_CLASS, chatEventText, chatEventView } from "./chat-event-meta";
import { clockLabel, customerName, moneyLabel } from "./chat-format";
import { CHAT_TEXT } from "./chat-texts";

const HISTORY_PREVIEW = 3;

interface Props {
  detail: ConversationDetail;
  feed: ChatFeedItem[];
  meUserId: string;
  busy: boolean;
  onTake: () => void;
  onTransfer: () => void;
  onOpenOrder: (() => void) | null;
  onOpenCustomer: (() => void) | null;
  onFocusEvent: (id: string) => void;
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-1 border-b border-outline-variant px-4 py-3 last:border-b-0">
      <h3 className="text-sm font-semibold text-on-surface">{title}</h3>
      {children}
    </section>
  );
}

function LinkRow({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="-mx-2 flex min-h-12 items-center justify-between rounded-lg px-2 text-sm text-link outline-none hover:bg-surface-low focus-visible:ring-2 focus-visible:ring-ring lg:min-h-11">
      {label}
      <ChevronRight className="size-4" aria-hidden />
    </button>
  );
}

function HistoryRow({ event, onClick }: { event: ConversationEventDto; onClick: () => void }) {
  const view = chatEventView(event);
  return (
    <button type="button" onClick={onClick} className="-mx-2 flex min-h-11 items-center gap-2 rounded-lg px-2 text-left text-xs outline-none hover:bg-surface-low focus-visible:ring-2 focus-visible:ring-ring">
      <span aria-hidden className={`inline-flex size-6 shrink-0 items-center justify-center rounded-full ${EVENT_TONE_CLASS[view.tone]}`}>
        <view.icon className="size-3.5" />
      </span>
      <span className="min-w-0 flex-1 truncate text-on-surface">{chatEventText(event)}</span>
      <span className="shrink-0 tabular-nums text-on-surface-variant">{clockLabel(event.at)}</span>
    </button>
  );
}

export function ConversationInfoPanel(props: Props) {
  const { detail, meUserId, busy } = props;
  const [allHistory, setAllHistory] = useState(false);
  const order = detail.contextOrder;
  const events = props.feed.flatMap((i) => (i.kind === "event" ? [i.event] : [])).reverse();
  const shown = allHistory ? events : events.slice(0, HISTORY_PREVIEW);
  const assignee = detail.assignedUser;

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
      <Section title={CHAT_TEXT.infoCustomer}>
        <p className="text-sm text-on-surface">{customerName(detail.customer)}</p>
        {detail.customer.phone ? <p className="text-sm tabular-nums text-on-surface-variant">{detail.customer.phone}</p> : null}
        {detail.customer.email ? <p className="truncate text-sm text-on-surface-variant">{detail.customer.email}</p> : null}
        <p className="text-xs text-on-surface-variant">{detail.marketingConsent.granted ? CHAT_TEXT.infoConsentYes : CHAT_TEXT.infoConsentNo}</p>
        {props.onOpenCustomer ? <LinkRow label={`${CHAT_TEXT.openCustomer}`} onClick={props.onOpenCustomer} /> : null}
      </Section>

      {order ? (
        <Section title={CHAT_TEXT.infoOrder(order.number)}>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="neutral">{order.stageName}</Badge>
            <span className="text-sm tabular-nums text-on-surface">{moneyLabel(order.totalGross, order.currency)}</span>
          </div>
          {order.owner ? <p className="text-xs text-on-surface-variant">{CHAT_TEXT.infoOrderLeads(order.owner.name)}</p> : null}
          {props.onOpenOrder ? <LinkRow label={CHAT_TEXT.openOrder} onClick={props.onOpenOrder} /> : null}
        </Section>
      ) : null}

      <Section title={CHAT_TEXT.infoDialog}>
        <p className={assignee ? "text-sm text-on-surface" : "text-sm text-warning"}>
          {CHAT_TEXT.leadsNow}: {assignee ? (assignee.id === meUserId ? "вы" : assignee.name) : CHAT_TEXT.nobody}
        </p>
        {detail.actions.take || detail.actions.transfer ? (
          <div className="flex flex-wrap gap-2 pt-1">
            {detail.actions.take ? (
              <Button variant="soft" size="sm" className="min-h-12 lg:min-h-9" loading={busy} onClick={props.onTake}>
                {CHAT_TEXT.take}
              </Button>
            ) : null}
            {detail.actions.transfer ? (
              <Button variant="outline" size="sm" className="min-h-12 lg:min-h-9" onClick={props.onTransfer}>
                {CHAT_TEXT.transfer}
              </Button>
            ) : null}
          </div>
        ) : null}
      </Section>

      {events.length > 0 ? (
        <Section title={CHAT_TEXT.infoHistory}>
          {shown.map((e) => (
            <HistoryRow key={e.id} event={e} onClick={() => props.onFocusEvent(e.id)} />
          ))}
          {!allHistory && events.length > HISTORY_PREVIEW ? (
            <Button variant="link" className="self-start" onClick={() => setAllHistory(true)}>
              {CHAT_TEXT.infoShowAll(events.length)}
            </Button>
          ) : null}
        </Section>
      ) : null}

      <p className="px-4 py-3 text-xs text-on-surface-muted">{CHAT_TEXT.infoResponseTime}</p>
    </div>
  );
}
